import type { QueryResult } from '@dynatrace-sdk/client-query';
import { format, units, adjustFractionDigits } from '@dynatrace-sdk/units';

import { mapMetricUnitToSdk } from './unit-mapping.js';

/** @public */
export type DqlNumberType = 'double' | 'long' | 'duration';

/**
 * Interprets numbers from Grail and formats them according to its type.
 * All numbers of type `duration` are formatted in nanoseconds. Until the SDK
 * supports `BigInt`, `long` values that exceed the save integer space will be
 * formatted as strings.
 * @param value - The actual field value, either `number` or `string` for
 * `long` values.
 * @param type - The numeric DQL type, `double`, `long` or `duration`.
 * @param unit - Either the Units-SDK unit key of the value or the literal
 * configuration object accepted by the sdk/units format function.
 * @returns the formatted numeric value.
 * @throws error if the data is malformed
 * @public
 */
export function formatDqlNumber<T extends DqlNumberType>(
  value: string | number,
  type: T,
  unit?: T extends 'duration' ? never : string | Parameters<typeof format>[1],
): string {
  const asNumber =
    type === 'duration' || type === 'long'
      ? Number.parseInt(value as string)
      : Number.parseFloat(value as string);

  if (Number.isNaN(asNumber)) {
    return fallbackRenderer(asNumber);
  }

  if (type === 'duration') {
    return format(asNumber, {
      input: units.time.nanosecond,
      cascade: 3,
    });
  }

  // Currently no BigInt support; return as string if value exceeds MAX_SAFE_INTEGER
  if (
    type === 'long' &&
    (BigInt(value) < Number.MIN_SAFE_INTEGER ||
      BigInt(value) > Number.MAX_SAFE_INTEGER)
  ) {
    return fallbackRenderer(value);
  }

  if (typeof unit === 'string') {
    const mappedUnit = mapMetricUnitToSdk(unit);
    if (!mappedUnit) {
      return adjustFractionDigits(asNumber, {
        maximumFractionDigits: 3,
      });
    }

    return format(asNumber, {
      input: typeof mappedUnit === 'string' ? undefined : mappedUnit,
      maximumFractionDigits: type !== 'long' ? 3 : undefined,
    });
  }

  if (!unit) {
    return adjustFractionDigits(asNumber, {
      maximumFractionDigits: type !== 'long' ? 3 : undefined,
    });
  }

  if (!unit.input && unit.suffix === undefined) {
    const decimals = unit.maximumFractionDigits ?? 3;
    return adjustFractionDigits(asNumber, {
      maximumFractionDigits: decimals,
    });
  }

  return format(asNumber, unit);
}

/** @internal */
export const _getMetadataMetricsUnitForFieldName = (
  metadata: QueryResult['metadata'] | undefined,
  fieldName: string,
) => metadata?.metrics?.find((metric) => fieldName === metric.fieldName)?.unit;

/**
 * @public
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ConversionCellValue = any;

/**
 * @internal
 */
export const fallbackRenderer = (value: ConversionCellValue) => {
  return value.toString();
};
